import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CornerDownLeftIcon, MonitorIcon, MoonIcon, SearchIcon, SunIcon } from 'lucide-react';
import { NavGroup } from './OperatorShell';
import { useTheme } from '../../contexts/ThemeContext';
interface Command {
  id: string;
  label: string;
  group: string;
  run: () => void;
}

/**
 * Keyboard-first access to whatever this console actually contains.
 *
 * The command list is built from the SAME permission-filtered navigation the
 * sidebar renders — it is not a second register — so the palette can never
 * offer a destination the administrator's roles do not include, and renaming a
 * concept renames the commands with it.
 */
export function CommandPalette({
  groups
}: {
  groups: NavGroup[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const {
    setPreference,
    theme
  } = useTheme();
  useEffect(() => {
    const listen = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(wasOpen => !wasOpen);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', listen);
    return () => window.removeEventListener('keydown', listen);
  }, []);
  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
      return;
    }
    // Focus once, on open — not on every render, which would fight the typist.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);
  const commands = useMemo<Command[]>(() => {
    const destinations = groups.flatMap(group => group.items.map(item => ({
      id: `nav:${item.to}`,
      label: item.label,
      group: group.label,
      run: () => navigate(item.to)
    })));
    const appearance: Command[] = [{
      id: 'theme:system',
      label: `Appearance — follow system (currently ${theme})`,
      group: 'Appearance',
      run: () => setPreference('system')
    }, {
      id: 'theme:light',
      label: 'Appearance — light',
      group: 'Appearance',
      run: () => setPreference('light')
    }, {
      id: 'theme:dark',
      label: 'Appearance — dark',
      group: 'Appearance',
      run: () => setPreference('dark')
    }];
    return [...destinations, ...appearance];
  }, [groups, navigate, setPreference, theme]);
  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return commands.slice(0, 8);
    return commands.filter(command => command.label.toLowerCase().includes(needle) || command.group.toLowerCase().includes(needle)).slice(0, 10);
  }, [commands, query]);
  const chosen = results[Math.min(active, Math.max(results.length - 1, 0))];
  return <>
      <button type="button" onClick={() => setOpen(true)} className="hidden min-h-touch items-center gap-2 rounded-lg border border-line-strong bg-canvas px-3 text-body-sm text-ink-muted transition-colors duration-150 ease-out hover:text-ink md:flex" data-id="element-5123" data-mp="vuulkc3cmcul">
        <SearchIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-5124" data-mp="hvuu6wogbjuq" />
        <span data-id="element-5125" data-mp="d3a15xdddas9">Search this console</span>
        <kbd className="ml-1 rounded border border-line-strong bg-surface px-1.5 py-0.5 text-[0.6875rem] font-semibold text-ink-subtle" data-id="element-5126" data-mp="d28s1yfwyyd6">
          ⌘K
        </kbd>
      </button>

      <AnimatePresence data-id="element-5127" data-mp="36ouz8pm5tmu">
        {open && <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]" data-id="element-5128" data-mp="3v96ll7gd1kmf">
            <motion.div className="absolute inset-0 bg-ink/50" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease: 'easeOut'
        }} onClick={() => setOpen(false)} data-id="element-5129" data-mp="3vazi3tx9eogp" />
            <motion.div role="dialog" aria-label="Command palette" aria-modal="true" className="relative w-full max-w-xl overflow-hidden rounded-panel border border-line bg-surface shadow-xl" initial={{
          opacity: 0,
          scale: 0.96,
          y: -8
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.98,
          y: -4
        }} transition={{
          duration: 0.16,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-5130" data-mp="1khdrl1kzlucy">
              <div className="flex items-center gap-2 border-b border-line px-4" data-id="element-5131" data-mp="16h2xzqsam1o3">
                <SearchIcon className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" data-id="element-5132" data-mp="2vwavfhltyo4d" />
                <input ref={inputRef} value={query} onChange={event => {
              setQuery(event.target.value);
              setActive(0);
            }} onKeyDown={event => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive(index => Math.min(index + 1, results.length - 1));
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive(index => Math.max(index - 1, 0));
              }
              if (event.key === 'Enter' && chosen) {
                event.preventDefault();
                chosen.run();
                setOpen(false);
              }
            }} placeholder="Go to a section, or change appearance" aria-label="Search commands" className="min-h-touch-lg w-full border-0 bg-transparent text-body text-ink outline-none placeholder:text-ink-subtle" data-id="element-5133" data-mp="2vugaw4eh0y0t" />
              </div>

              {results.length === 0 ? <p className="px-4 py-6 text-body-sm text-ink-muted" data-id="element-5134" data-mp="c92n6fgfc5v0">
                  Nothing here matches “{query.trim()}”. Only sections your roles permit are listed.
                </p> : <ul className="max-h-[52vh] overflow-y-auto py-1.5" data-id="element-5135" data-mp="m81ofcyv0z7z">
                  {results.map((command, index) => <li key={command.id} data-id="element-5136" data-mp="q8c45hcgf401" data-mpi={index}>
                      <button type="button" onMouseEnter={() => setActive(index)} onClick={() => {
                command.run();
                setOpen(false);
              }} className={`flex min-h-touch w-full items-center gap-3 px-4 text-left text-body-sm transition-colors duration-150 ease-out ${index === active ? 'bg-brand-50 text-brand-800' : 'text-ink hover:bg-canvas'}`} data-id="element-5137" data-mp="q79cv8b7yzjl">
                        {command.group === 'Appearance' ? <AppearanceIcon label={command.label} data-id="element-5138" data-mp="2ue2ekzn7t4r0" /> : <span className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-5139" data-mp="2pf93tezbbruj" />}
                        <span className="min-w-0 flex-1 truncate" data-id="element-5140" data-mp="2khzedzbxbg6p">{command.label}</span>
                        <span className="shrink-0 text-caption text-ink-subtle" data-id="element-5141" data-mp="3i4zhjmx18rq4">
                          {command.group}
                        </span>
                        {index === active && <CornerDownLeftIcon className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" data-id="element-5142" data-mp="38dv26m3g34ur" />}
                      </button>
                    </li>)}
                </ul>}
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}
function AppearanceIcon({
  label
}: {
  label: string;
}) {
  const Icon = label.includes('dark') ? MoonIcon : label.includes('light') ? SunIcon : MonitorIcon;
  return <Icon className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" data-id="element-5143" data-mp="2vghpxv9pe16g" />;
}